//Node Modules
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import ErrorMessage from "./ErrorMessage";

//Helpers
import { useIsValidSession, useLogout } from "../../helpers/Auth";

//Atoms
import { selectedUserSchemeAtom, userSchemeOptionsAtom } from "../../atoms/user";

//Other
import { nav, adminRoutes, ownerRoutes, tenantRoutes } from "../../config/navigation";

const AuthRoute = () => {
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const isValidSession = useIsValidSession();
	const handleLogout = useLogout();

	//Recoil State
	const [scheme, setScheme] = useRecoilState(selectedUserSchemeAtom);
	const schemeOptions = useRecoilValue(userSchemeOptionsAtom);

	// Local State
	const [authState, setAuthState] = useState({ status: "loading" });

	useEffect(() => {
		let isMounted = true;

		const determineRoute = async () => {
			if (!isMounted) return;
			setAuthState((prev) => (prev.status === "loading" ? prev : { status: "loading" }));

			if ((await isValidUser()) === false) {
				console.warn("No valid user. Navigate back to the login page.");
				if (!isMounted) return;
				setAuthState({
					status: "navigate",
					to: nav.guest.login,
					options: { state: { from: location }, replace: true },
				});
				return;
			}

			if (!scheme) {
				console.warn("Scheme is null so signout!");
				await doLogout();
				if (!isMounted) return;
				setAuthState({
					status: "navigate",
					to: nav.guest.login,
					options: { state: { from: location }, replace: true },
				});
				return;
			}

			const rootPathSegment = location.pathname.split("/")[1] || "";
			const rootLocation = `/${rootPathSegment}`;
			let validRole = true;

			if (scheme.role === "ADMINISTRATOR" && !adminRoutes.includes(rootLocation)) validRole = false;
			if (scheme.role === "OWNER" && !ownerRoutes.includes(rootLocation)) validRole = false;
			if (scheme.role === "TENANT") {
				const tenantDefaultRoute = `${nav.device.base}/${nav.device.management}`;
				if (rootLocation === "/" || rootLocation === nav.home) {
					if (!isMounted) return;
					setAuthState({ status: "navigate", to: tenantDefaultRoute, options: { replace: true } });
					return;
				}
				if (!tenantRoutes.includes(rootLocation)) validRole = false;
			}

			if (validRole) {
				const urlScheme = searchParams.get("schemeId");
				if (urlScheme && scheme.id !== urlScheme) {
					const updatedScheme = schemeOptions.find((s) => s.id === urlScheme);
					if (updatedScheme) setScheme(updatedScheme);
				}
				if (!isMounted) return;
				setAuthState({ status: "authorized" });
				return;
			}

			console.warn("Navigate to user route location...");
			const userRoleBaseRoute =
				scheme.role === "ADMINISTRATOR"
					? adminRoutes[0]
					: scheme.role === "TENANT"
						? `${nav.device.base}/${nav.device.management}`
						: ownerRoutes[0];
			const error = new Error(`Invalid role (${scheme.role}) for route ${rootLocation}`);
			if (!isMounted) return;
			setAuthState({
				status: "error",
				error,
				redirectTo: userRoleBaseRoute,
			});
		};

		determineRoute();

		return () => {
			isMounted = false;
		};
	}, [location, scheme, schemeOptions, isValidSession]);

	useEffect(() => {
		if (authState.status === "navigate") {
			navigate(authState.to, authState.options);
		}
	}, [authState, navigate]);

	const isValidUser = async () => {
		const value = await isValidSession();
		return value;
	};

	const doLogout = async () => {
		await handleLogout();
		return;
	};

	if (authState.status === "loading" || authState.status === "navigate") return null;

	if (authState.status === "error") {
		return (
			<ErrorMessage
				error={authState.error}
				message="You do not have permission to view this page."
				onRetry={() => setAuthState({ status: "navigate", to: authState.redirectTo, options: { replace: true } })}
				retryLabel="Go home"
			/>
		);
	}

	return <Outlet />;
};

export default AuthRoute;
